/**
 * Account Quick Actions Component
 *
 * Dropdown menu with account lifecycle actions.
 * Actions are conditionally shown based on current account status.
 */
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  Modal,
  Input,
  Label,
  useToast,
} from '@respectly/ui';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  PauseCircle,
  PlayCircle,
  XCircle,
  UserCog,
  ScrollText,
  Trash2,
  DownloadCloud,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { AccountListItem } from '@respectly/shared-types';
import { useAccountsStore } from '../../stores/useAccountsStore';
import { ImportContactsToAccountDialog } from './ImportContactsToAccountDialog';
import { DeleteAccountsDialog } from './DeleteAccountsDialog';

// ============================================
// Types
// ============================================

interface AccountQuickActionsProps {
  account: AccountListItem;
  onImpersonate?: () => void;
}

interface ActionModalState {
  type: 'suspend' | 'cancel' | 'delete' | null;
  reason: string;
}

// ============================================
// Component
// ============================================

export function AccountQuickActions({ account, onImpersonate }: AccountQuickActionsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    suspendAccount,
    reactivateAccount,
    cancelAccount,
    deleteAccount,
    isPerformingAction,
    refreshAccounts,
  } = useAccountsStore();

  const [modalState, setModalState] = useState<ActionModalState>({
    type: null,
    reason: '',
  });
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Determine available actions based on status
  const canSuspend = account.status === 'active' || account.status === 'trial';
  const canReactivate = account.status === 'suspended';
  const canCancel = account.status !== 'cancelled';

  // ============================================
  // Handlers
  // ============================================

  const handleViewDetails = () => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  const handleEdit = () => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  const handleViewAudit = () => {
    navigate({ to: '/accounts/$id/$tab', params: { id: account.id, tab: 'audit' } });
  };

  const handleSuspend = async () => {
    if (!modalState.reason.trim()) return;

    const success = await suspendAccount(account.id, {
      reason: modalState.reason,
      notifyUsers: true,
    });

    if (success) {
      closeModal();
    }
  };

  const handleReactivate = async () => {
    const success = await reactivateAccount(account.id, {
      notifyUsers: true,
    });

    if (success) {
      closeModal();
    }
  };

  const handleCancel = async () => {
    if (!modalState.reason.trim()) return;

    const success = await cancelAccount(account.id, {
      reason: modalState.reason,
    });

    if (success) {
      closeModal();
    }
  };

  const handleDelete = async () => {
    const success = await deleteAccount(account.id);

    if (success) {
      closeModal();
      toast({ title: 'Account deleted', description: `"${account.name}" was permanently deleted.` });
    } else {
      toast({
        title: 'Delete failed',
        description: useAccountsStore.getState().actionError?.message ?? 'Could not delete account',
        variant: 'error',
      });
    }
  };

  const openModal = (type: 'suspend' | 'cancel' | 'delete') => {
    setModalState({ type, reason: '' });
  };

  const closeModal = () => {
    setModalState({ type: null, reason: '' });
  };

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* View/Edit Actions */}
          <DropdownMenuItem onClick={handleViewDetails}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEdit}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Account
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Lifecycle Actions */}
          {canSuspend && (
            <DropdownMenuItem
              onClick={() => openModal('suspend')}
              className="text-amber-600 focus:text-amber-600"
            >
              <PauseCircle className="mr-2 h-4 w-4" />
              Suspend Account
            </DropdownMenuItem>
          )}
          {canReactivate && (
            <DropdownMenuItem
              onClick={handleReactivate}
              className="text-emerald-600 focus:text-emerald-600"
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              Reactivate Account
            </DropdownMenuItem>
          )}
          {canCancel && (
            <DropdownMenuItem
              onClick={() => openModal('cancel')}
              className="text-red-600 focus:text-red-600"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Account
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {/* Impersonation */}
          {onImpersonate && (
            <DropdownMenuItem onClick={onImpersonate}>
              <UserCog className="mr-2 h-4 w-4" />
              Impersonate User
            </DropdownMenuItem>
          )}

          {/* Audit */}
          <DropdownMenuItem onClick={handleViewAudit}>
            <ScrollText className="mr-2 h-4 w-4" />
            View Audit Log
          </DropdownMenuItem>

          {/* Seed this account with every tenant as a contact (marketing) */}
          <DropdownMenuItem onClick={() => setIsImportOpen(true)}>
            <DownloadCloud className="mr-2 h-4 w-4" />
            Import contacts
          </DropdownMenuItem>

          {/* Permanent delete (any account status) */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => openModal('delete')}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Account
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Suspend Modal */}
      <Modal isOpen={modalState.type === 'suspend'} onClose={closeModal} title="Suspend Account">
        <p className="mb-4 text-sm text-slate-500">
          Are you sure you want to suspend "{account.name}"? Users will lose access until
          reactivated.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="suspend-reason">Reason for suspension *</Label>
            <Input
              id="suspend-reason"
              placeholder="Enter reason for suspension..."
              value={modalState.reason}
              onChange={(e) => setModalState((s) => ({ ...s, reason: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspend}
              disabled={!modalState.reason.trim() || isPerformingAction}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isPerformingAction ? 'Suspending...' : 'Suspend Account'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Modal */}
      <Modal isOpen={modalState.type === 'cancel'} onClose={closeModal} title="Cancel Account">
        <p className="mb-4 text-sm text-slate-500">
          Are you sure you want to cancel "{account.name}"? This action cannot be easily undone.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason for cancellation *</Label>
            <Input
              id="cancel-reason"
              placeholder="Enter reason for cancellation..."
              value={modalState.reason}
              onChange={(e) => setModalState((s) => ({ ...s, reason: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={!modalState.reason.trim() || isPerformingAction}
            >
              {isPerformingAction ? 'Cancelling...' : 'Cancel Account'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <DeleteAccountsDialog
        isOpen={modalState.type === 'delete'}
        onClose={closeModal}
        onConfirm={handleDelete}
        isDeleting={isPerformingAction}
        count={1}
        accounts={[{ id: account.id, name: account.name, status: account.status }]}
      />

      {/* Import all tenants into this account (pre-selected + locked) */}
      <ImportContactsToAccountDialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        preselectedAccount={account}
        onImported={refreshAccounts}
      />
    </>
  );
}
